import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type TempUnit = 'C' | 'F';

interface UIState {
  sidebarOpen: boolean;
  language: 'es' | 'en';
  viewMode: 'operator' | 'engineer' | 'auditor';
  theme: 'dark';
  tempUnit: TempUnit;

  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setLanguage: (lang: 'es' | 'en') => void;
  setViewMode: (mode: 'operator' | 'engineer' | 'auditor') => void;
  toggleTempUnit: () => void;
}

/** Convert °C to the user's selected unit */
export function formatTemp(celsius: number, unit: TempUnit): string {
  if (unit === 'F') return `${((celsius * 9) / 5 + 32).toFixed(1)}°F`;
  return `${celsius.toFixed(1)}°C`;
}

/** Format a temperature delta (ΔT). 1 K = 1 °C = 1.8 °F-delta. */
export function formatTempDelta(deltaK: number, unit: TempUnit, digits = 1): string {
  if (unit === 'F') return `${((deltaK * 9) / 5).toFixed(digits)}°F`;
  return `${deltaK.toFixed(digits)}K`;
}

/** Unit label for the current setting */
export function tempLabel(unit: TempUnit): string {
  return unit === 'F' ? '°F' : '°C';
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      language: 'es',
      viewMode: 'engineer',
      theme: 'dark',
      tempUnit: 'C' as TempUnit,

      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setLanguage: (lang) => set({ language: lang }),
      setViewMode: (mode) => set({ viewMode: mode }),
      toggleTempUnit: () => set((state) => ({ tempUnit: state.tempUnit === 'C' ? 'F' : 'C' })),
    }),
    {
      name: 'inverterai-ui',
      partialize: (state) => ({
        sidebarOpen: state.sidebarOpen,
        language: state.language,
        viewMode: state.viewMode,
        tempUnit: state.tempUnit,
      }),
    }
  )
);
